import React, { FC, useLayoutEffect, useRef, useState } from 'react'
import { Spin } from 'antd'
import { FileDeliveryProgress } from '../../../api/forum-upload'
import { Button, Tooltip } from '../index'
import { Play, Close, Ok } from '../icons'
import { Title } from '../text'
import PopupGallery, { MediaAuthor } from '../../gallery/popup-gallery/popup-gallery'
import FileExtension from './file-extension'
import styles from './file-preview.module.scss'
import './flie-preview.ant.scss'

type FilePreviewProps = {
  name?: string
  url?: string
  type?: string
  thumbScr?: string
  size?: string
  status?: 0 | 1 | 2
  videoManifest?: string
  onRemove?: () => void
  unclickable?: boolean
  isBig?: boolean
  uploadProgress?: FileDeliveryProgress
  uploadError?: boolean
  onOpen?: () => void
  author?: MediaAuthor | null
}

const FilePreview: FC<FilePreviewProps> = (props) => {
  const {
    name,
    type,
    thumbScr,
    onRemove,
    size,
    status,
    videoManifest,
    url,
    unclickable,
    isBig,
    uploadProgress,
    uploadError,
    onOpen,
    author,
  } = props

  const [popupShow, setPopupShow] = useState(false)
  const openMedia = () => {
    if (onOpen) onOpen()
    else setPopupShow(true)
  }
  const [doRemove, setDoRemove] = useState(false)
  const [previewShape, setPreviewShape] = useState<{ src?: string; ratio: number }>({ ratio: 1 })
  const previewRef = useRef<HTMLDivElement | null>(null)
  const [nameTruncated, setNameTruncated] = useState(false)
  const [actionHovered, setActionHovered] = useState(false)
  const hoverEvents = {
    onMouseEnter: () => setActionHovered(true),
    onMouseLeave: () => setActionHovered(false),
  }
  const typeImage = /image*|image/gm.test(type || '')
  const typeVideo = /video*|video/gm.test(type || '')
  const typeMedia = typeImage || typeVideo
  const showMetadataOverlay = !!(isBig && typeMedia && status === 1 && thumbScr && !uploadProgress)
  const canOpenOverlay = showMetadataOverlay && !unclickable && !!url

  useLayoutEffect(() => {
    const element = previewRef.current
    const heading = element?.querySelector<HTMLElement>('h5')
    const title =
      heading?.querySelector<HTMLElement>('[data-file-name-prefix]') ||
      heading?.querySelector<HTMLElement>('[data-file-action]') ||
      heading
    if (!element || !title) return

    const update = () => setNameTruncated(title.scrollWidth > title.clientWidth + 1)
    update()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    const observer = new ResizeObserver(update)
    observer.observe(element)
    observer.observe(title)
    return () => observer.disconnect()
  }, [name, status, url, unclickable, type, showMetadataOverlay])

  const previewSrc = thumbScr
  const proportional = isBig && status === 1 && !!previewSrc
  const previewWidth = previewShape.src === previewSrc ? 250 * Math.max(1, previewShape.ratio) : 250
  const extension = name?.match(/\.[a-z0-9]+$/i)?.[0].toLowerCase() || 'file'
  const extensionBadge = <FileExtension value={extension} />
  const fileAction = (content: React.ReactNode, label?: string) => {
    if (status !== 1 || unclickable || !url) return content
    return typeMedia ? (
      <button
        type="button"
        className={styles.fileAction}
        data-file-action
        aria-label={label}
        {...hoverEvents}
        onClick={openMedia}
      >
        {content}
      </button>
    ) : (
      <a
        href={url}
        rel="noopener noreferrer"
        target="_blank"
        download={name}
        className={styles.fileAction}
        data-file-action
        aria-label={label}
        {...hoverEvents}
      >
        {content}
      </a>
    )
  }

  const nameLabel =
    name && name.length > 6 ? (
      <span className={styles.fileNameLabel}>
        <span className={styles.fileNamePrefix} data-file-name-prefix>
          {name.slice(0, -6)}
        </span>
        <span className={styles.fileNameSuffix}>{name.slice(-6)}</span>
      </span>
    ) : (
      name
    )
  const renderName = (interactive: boolean) => (
    <Title level={5} className={styles.fileName}>
      {interactive ? fileAction(nameLabel, name) : nameLabel}
    </Title>
  )
  const renderSize = (interactive: boolean) =>
    size && <div className={styles.fileSize}>{interactive ? fileAction(size) : size}</div>

  const preview = (
    <div
      ref={previewRef}
      className={`${styles.attachment} ${isBig ? '' : styles.container} ${
        actionHovered && status === 1 && !unclickable && url ? styles.actionHovered : ''
      }`}
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={() => setActionHovered(false)}
    >
      <div
        style={proportional ? { width: previewWidth } : undefined}
        className={`${styles.thumbContainer} ${isBig ? styles.big : styles.small} ${
          proportional ? styles.proportional : ''
        }`}
        onClick={() => typeMedia && !unclickable && openMedia()}
        role={canOpenOverlay ? 'button' : undefined}
        tabIndex={canOpenOverlay ? 0 : undefined}
        aria-label={canOpenOverlay ? `Открыть ${name || 'вложение'}` : undefined}
        onKeyDown={(event) => {
          if (canOpenOverlay && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            event.stopPropagation()
            openMedia()
          }
        }}
        {...hoverEvents}
      >
        {uploadProgress && (uploadError ? <Close /> : uploadProgress.phase === 'done' ? <Ok /> : <Spin size="small" />)}
        {!uploadProgress && status === 0 && <Spin size={'small'} spinning={true} className={styles.spin} />}
        {status === 2 && <Close />}
        {typeMedia && status === 1 && (
          <div style={proportional ? undefined : { backgroundImage: `url("${thumbScr}")` }} className={styles.thumbPic}>
            {proportional && (
              <img
                src={previewSrc}
                alt={name || 'Превью вложения'}
                onLoad={({ currentTarget }) => {
                  if (currentTarget.naturalWidth && currentTarget.naturalHeight) {
                    setPreviewShape({
                      src: previewSrc,
                      ratio: currentTarget.naturalWidth / currentTarget.naturalHeight,
                    })
                  }
                }}
              />
            )}
            {showMetadataOverlay && (
              <div className={styles.mediaMetadata}>
                {renderName(false)}
                {renderSize(false)}
              </div>
            )}
            {typeVideo && (
              <span className={styles.playIcon}>
                <Play color="currentColor" />
              </span>
            )}
          </div>
        )}
        {!typeMedia && status === 1 && (
          <>
            {url && !unclickable ? (
              <a
                href={url}
                rel="noopener noreferrer"
                target="_blank"
                className={styles.link}
                download={name}
                aria-label={`Скачать ${name || 'файл'}`}
              >
                {extensionBadge}
              </a>
            ) : (
              extensionBadge
            )}
          </>
        )}
      </div>
      <div className={styles.description}>
        {!showMetadataOverlay && renderName(true)}
        {onRemove &&
          (doRemove ? (
            <span className={styles.acceptDialog}>
              <Button onClick={onRemove} type="link" className={styles.accept}>
                Подтвердить
              </Button>
              <Button onClick={() => setDoRemove(false)} type="link" className={styles.cancel}>
                Отменить
              </Button>
            </span>
          ) : (
            <Button onClick={() => setDoRemove(true)} type="link" className="btn-delete">
              Удалить
            </Button>
          ))}
        {!showMetadataOverlay && renderSize(true)}
        {uploadProgress && (
          <div className={styles.uploadProgress}>
            <span>
              {uploadError
                ? 'Загрузка прервана'
                : uploadProgress.phase === 'done'
                ? 'Загружено'
                : uploadProgress.phase === 'processing'
                ? 'Обработка на сервере…'
                : uploadProgress.phase === 'queued'
                ? 'Ожидает загрузки'
                : uploadProgress.phase === 'preparing'
                ? 'Сохранение файла…'
                : `Загрузка · ${Math.floor((uploadProgress.loaded / Math.max(uploadProgress.total, 1)) * 100)}%`}
            </span>
            <progress
              aria-label={`Загрузка файла ${name || ''}`}
              max={Math.max(uploadProgress.total, 1)}
              value={uploadProgress.loaded}
            />
          </div>
        )}
        {!uploadProgress && status === 0 && <span className={styles.processing}>Обработка файла…</span>}
        {!uploadProgress && status === 2 && <span className={styles.processing}>Не удалось обработать файл</span>}
      </div>

      {typeMedia && !onOpen && (
        <>
          {status === 1 && (
            <PopupGallery
              label={name}
              author={author}
              captions={author !== undefined ? [name || ''] : undefined}
              visible={popupShow}
              onCancel={() => setPopupShow(false)}
            >
              {typeVideo ? (
                <PopupGallery.Video src={url} videoManifest={videoManifest} thumbnail={thumbScr} />
              ) : (
                <PopupGallery.Image src={url || ''} alt={name || ''} />
              )}
            </PopupGallery>
          )}
        </>
      )}
    </div>
  )

  return (
    <Tooltip
      title={
        nameTruncated
          ? name?.split(/(\s+)/).map((part, index) =>
              /\s/.test(part) ? (
                part
              ) : (
                <span key={index} className={styles.nameWord}>
                  {part}
                </span>
              ),
            )
          : undefined
      }
      trigger={nameTruncated ? 'hover' : []}
      placement="bottom"
      overlayClassName={`ratingModuleItemTooltip ${styles.nameTooltip}`}
    >
      {preview}
    </Tooltip>
  )
}

export default FilePreview
